import { VerifyConfig } from "./VerifyConfig";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useConfigSyncStatus } from "@/hooks/useConfigSyncStatus";
import { getBasePath } from "@/utils/runtimeConfig";

function formatTimestamp(value?: string | null): string {
  if (!value) return "never";
  const date = new Date(value);
  return isNaN(date.getTime()) ? value : date.toLocaleString();
}

/**
 * Opens the tenant's own admin, which is where its rollouts and operations
 * are. Each tenant has a router of its own, so this is a page load, as in the
 * tenants list, not a navigation inside the current router.
 */
function TenantLink({ id }: { id: string }) {
  return (
    <a
      href={`${getBasePath()}/${encodeURIComponent(id)}`}
      className="underline-offset-4 hover:underline"
    >
      {id}
    </a>
  );
}

const short = (hash?: string | null) => (hash ? hash.slice(0, 12) : "none");

/**
 * Where a tenant's config comes from and whether it is current: its parent,
 * the sync mode, when it was last synced and, for a synced tenant, whether
 * the parent chain has moved on since (drift).
 */
export function SyncStatusCard({
  tenantId,
  refreshToken,
}: {
  tenantId?: string;
  refreshToken?: string;
}) {
  const { status, loading } = useConfigSyncStatus(tenantId, refreshToken);
  if (loading || !status) return null;

  const synced = status.config_sync === "synced";
  const detached = status.config_sync === "detached";

  return (
    <Card data-testid="sync-status-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Config sync
          {synced ? (
            status.drifted ? (
              <Badge variant="destructive">Out of date</Badge>
            ) : (
              <Badge variant="outline">In sync</Badge>
            )
          ) : detached ? (
            <Badge variant="secondary">Detached</Badge>
          ) : (
            <Badge variant="secondary">Inherit on read</Badge>
          )}
        </CardTitle>
        <CardDescription>
          {synced
            ? "This tenant keeps its own copy of the config it inherits."
            : detached
              ? "This tenant has left its parent: it reads only its own config and nothing is synced into it."
              : "Inherited config is merged in at request time; nothing is copied."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Parent</dt>
          <dd>
            {(status.parent_tenant_id ?? status.chain[0]) ? (
              <TenantLink id={(status.parent_tenant_id ?? status.chain[0])!} />
            ) : (
              "none"
            )}
          </dd>
          {status.chain.length > 1 && (
            <>
              <dt className="text-muted-foreground">Inherits from</dt>
              <dd className="flex flex-wrap gap-x-1">
                {status.chain.map((id, i) => (
                  <span key={id}>
                    {i > 0 && "\u2192 "}
                    <TenantLink id={id} />
                  </span>
                ))}
              </dd>
            </>
          )}
          {synced && (
            <>
              <dt className="text-muted-foreground">Last synced</dt>
              <dd>{formatTimestamp(status.config_synced_at)}</dd>
              <dt className="text-muted-foreground">Applied</dt>
              <dd className="font-mono">{short(status.config_sync_hash)}</dd>
              <dt className="text-muted-foreground">Current</dt>
              <dd className="font-mono">{short(status.expected_hash)}</dd>
            </>
          )}
        </dl>
        {!detached && <VerifyConfig tenantId={status.tenant_id} />}
      </CardContent>
    </Card>
  );
}
